import { Component, Input, OnDestroy } from '@angular/core';
import { BUTTON_CANCEL, BUTTON_SAVE, DefaultFormDialogComponent } from '../../../../../shared/message/dialogs/default-form-dialog.component';
import { Override } from '../../../../../shared/app-decorators';
import { GaValidateService } from '../../../../../shared/formly/ga-validate.service';
import { TranslationService } from '../../../../../shared/translation/translation.service';
import { ICompany, ICompanyCountry, IValueObject } from '../../../../../../type_definitions/datatypes';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../../../shared/formly/ga-formly.service';
import { FormattingService } from '../../../../../shared/services/formatting.service';
import { LOV_TIER_STATUS, LovService } from '../../../../../shared/services/lov.service';
import * as _ from 'underscore';
import { BPProfilesOEMService} from '../bp-profiles-oem.service';
import { BPProfilesOEMDataService, IOEMRelation, OEMTierStatus } from '../bp-profiles-oem-data.service';
import { CompanyDataService } from '../../../../../shared/company/company-data.service';
import { IDialogButton } from '../../../../../shared/message/ga-message.service';
import { Subscription } from 'rxjs';

export interface IOEMTierStatusDialogModel {
  tierStatus:IValueObject;
  distributor?:ICompanyCountry;
  comments?:string;
  oem?:ICompany;
}

export enum TierStatusDialogMode {
  NEW = 'new',
  EDIT = 'edit'
}

@Component({
  selector: 'oem-change-tier-status-dialog',
  templateUrl: '../../../../../shared/message/dialogs/default-form-dialog.html'
})
export class OEMTierStatusDialogComponent extends DefaultFormDialogComponent implements OnDestroy {
  @Input() public selectedOEM:IOEMRelation;
  @Input() public hideCommentsField:boolean;
  public model:IOEMTierStatusDialogModel;

  private tierStatusOptions:IValueObject[];
  private tierStatusRadioField:IExtraFieldConfigurationObject;
  private distributorField:IExtraFieldConfigurationObject;
  private oemField:IExtraFieldConfigurationObject;
  private valueChangesSubscription:Subscription;

  constructor(
    private formattingService:FormattingService,
    private formlyService:GaFormlyService,
    private lovService:LovService,
    private companyDataService:CompanyDataService,
    private bpProfilesOEMService:BPProfilesOEMService,
    private bpProfilesOEMDataService:BPProfilesOEMDataService,
    protected validationService:GaValidateService,
    protected translationService:TranslationService
  ) {
    super(validationService, translationService);
  }

  @Override(DefaultFormDialogComponent)
  public ngOnInit():void {
    super.ngOnInit();

    if (this.adding) {
      this.replaceButtonLabels({
        [BUTTON_CANCEL]: {
         translateText: 'GA.BUTTONS.DONT_LABEL',
         translateValues: { label: 'GA.BUTTONS.ADD' }
        }
      });
    } else {
      this.replaceButtonLabels({
        [BUTTON_SAVE]: {
          translateText: 'GA.BUTTONS.CHANGE_LABEL',
          translateValues: { label: 'BPPROFILES.OEM.TIERSTATUS.LABEL' }
        },
        [BUTTON_CANCEL]: {
          translateText: 'GA.BUTTONS.DONT_LABEL',
          translateValues: { label: 'GA.BUTTONS.CHANGE' }
        }
      });
    }

    if (this.selectedOEM) {
      this.model.tierStatus = _.clone(this.selectedOEM.tierStatus);
      this.model.distributor = _.findWhere(this.selectedOEM.distributors, { preferred: true })?.companyCountry;
    }

    // create fields
    this.fields = this.createFields();
    // fill fields
    this.fillFields();
    // register listener
    this.listen();
  }

  private listen():void {
    this.valueChangesSubscription = this.form.valueChanges.subscribe(() => {
      this.checkSaveButtonDisability();
    });
  }

  private checkSaveButtonDisability():void {
    this.validateFields(false)
      .then(() => {
        _.find(this.buttons, (button:IDialogButton) => button.id === BUTTON_SAVE).disabled = false;
      })
      .catch(() => {
        _.find(this.buttons, (button:IDialogButton) => button.id === BUTTON_SAVE).disabled = true;
      });
  }

  @Override(DefaultFormDialogComponent)
  public getModel():IOEMTierStatusDialogModel {
    const model:IOEMTierStatusDialogModel = _.clone(this.model);
    model.tierStatus = _.findWhere(this.tierStatusOptions, { id: model.tierStatus?.id });
    return model;
  }

  private fillFields():void {
    this.formlyService.fillField(this.oemField, this.companyDataService.listVendors());
    this.formlyService.fillField(
      this.tierStatusRadioField,
      this.lovService.list(LOV_TIER_STATUS)
        .then((tierStatusOptions:IValueObject[]) => {
          this.tierStatusOptions = tierStatusOptions;
          return this.bpProfilesOEMService.processTierStatuses(tierStatusOptions, true, true);
        })
    );

    this.formlyService.fillField(this.distributorField, this.bpProfilesOEMDataService.getCountryDistributors());
  }

  private createFields():IExtraFieldConfigurationObject[] {
    const fullWidthClass:string = 'col-sm-12';

    return [
      this.oemField = this.formlyService.aSearch('oem')
        .isRequired()
        .withFormat(this.formattingService.formatCompany)
        .withTranslationScreen('BPPROFILES.OEM')
        .withHideExpression(() => !this.adding)
        .withLabelClass(fullWidthClass)
        .withValueClass(fullWidthClass)
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .build(),
      this.formlyService.aGroup('tierStatusGroup', 'BPPROFILES.OEM.TIERSTATUS.LABEL')
        .withLabelClass('d-none')
        .withTranslationScreen('BPPROFILES.OEM')
        .withValueClass(fullWidthClass)
        .withFields([
          this.tierStatusRadioField = this.formlyService.aRadio('tierStatus')
            .isRequired()
            .withoutWrapper()
            .withValueClass(fullWidthClass)
            .withLabelProp('value')
            .onChange(() => {
              // add timeout here to wait distributor field rendered on screen
              setTimeout(() => {
                this.checkSaveButtonDisability();
              });
            })
            .build()
        ])
        .build(),
      this.distributorField = this.formlyService.aNgSelect('distributor')
        .withTranslationScreen('BPPROFILES.OEM')
        .isRequired()
        .withLabelClass(fullWidthClass)
        .withValueClass(fullWidthClass)
        .withFormat(this.formattingService.formatCompanyCountry)
        .withHideExpression(() => this.model.tierStatus?.id !== OEMTierStatus.DISTRIBUTOR)
        .withExtraOptions({
          templateOptions: {
            localSearch: true
          }
        })
        .build(),
      this.formlyService.aTextarea('comments')
        .withTranslationScreen('BPPROFILES.OEM')
        .withLabelClass('d-none')
        .withValueClass(fullWidthClass)
        .withPlaceholder('GA.GENERAL.COMMENTS')
        .withHideExpression(() => this.hideCommentsField)
        .build()
    ];
  }

  public ngOnDestroy():void {
    this.valueChangesSubscription?.unsubscribe();
  }
}